import type { LDClient } from 'launchdarkly-js-client-sdk';

interface LaunchDarklyContext {
  key: string;
  email: string;
  role: string;
}

/**
 * Seconds the loader waits for LaunchDarkly before giving up and letting each call
 * site fall back to its default. Without a bound, `waitForInitialization()` waits
 * forever, so a stalled connection held every loader that reads a flag — and the
 * `catch` in {@link checkFeatureFlag} was unreachable.
 */
const INITIALIZATION_TIMEOUT_SECONDS = 5;

let ldClientInstance: LDClient | null = null;
let initializationPromise: Promise<LDClient> | null = null;

/**
 * Get or create a LaunchDarkly client instance
 * This ensures we reuse the same client instance across the app
 */
export async function getLaunchDarklyClient(context: LaunchDarklyContext): Promise<LDClient> {
  // If we already have a client for this context, return it
  if (ldClientInstance) {
    // Check if the context has changed - if so, we need a new client
    const currentContext = ldClientInstance.getContext();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- LaunchDarkly context typing is complex
    const currentContextData = currentContext as any;
    if (
      currentContextData?.key === context.key &&
      currentContextData?.email === context.email &&
      currentContextData?.role === context.role
    ) {
      return ldClientInstance;
    }
    // Context changed, close the old client
    await ldClientInstance.close();
    ldClientInstance = null;
    initializationPromise = null;
  }

  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start new initialization. The promise is cached so concurrent callers share one
  // client, but only while it is pending or fulfilled. A rejected one left in the
  // cache is handed to every later caller, which pins every flag to its default for
  // the rest of the session.
  const pending = initializeLaunchDarklyClient(context);
  initializationPromise = pending;

  try {
    ldClientInstance = await pending;
  } catch (error) {
    if (initializationPromise === pending) {
      initializationPromise = null;
    }
    throw error;
  }

  return ldClientInstance;
}

/**
 * Initialize a new LaunchDarkly client
 */
async function initializeLaunchDarklyClient(context: LaunchDarklyContext): Promise<LDClient> {
  const clientId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  if (!clientId) {
    throw new Error('LaunchDarkly client ID not found in environment variables');
  }

  const { initialize } = await import('launchdarkly-js-client-sdk');

  const client = initialize(
    clientId,
    // Same context object the React provider in `_app.tsx` builds, `kind` and key
    // order included. The bootstrap cache below is stored under
    // `ld:<env>:<btoa(JSON.stringify(context))>`, so a context that serialises
    // differently reads a different cache -- and the two clients then disagree
    // about the same flag for the same user.
    {
      kind: 'user',
      key: context.key,
      email: context.email,
      role: context.role,
    },
    // Bootstrap parity with the provider. Without it this client starts with no flags
    // while the provider starts from cache, so a loader guard and the component that
    // navigated into it can read the same flag opposite ways.
    { bootstrap: 'localStorage' },
  );

  try {
    await client.waitForInitialization(INITIALIZATION_TIMEOUT_SECONDS);
  } catch (error) {
    // The SDK's timeout is a `Promise.race` against the init promise: it rejects and
    // leaves the client streaming and retrying in the background. This reference is
    // about to be dropped — `getLaunchDarklyClient` starts a fresh client on the next
    // call — so without closing it the connection and its event flush timer outlive
    // every failed attempt, and a flaky network accumulates them for the session.
    //
    // Safe for the bootstrap cache the loader and the provider share: `close()` empties
    // the in-memory flag store and never touches the persisted one.
    await client.close().catch(() => {});
    throw error;
  }

  return client;
}

/**
 * Check if a feature flag is enabled for the given context
 */
export async function checkFeatureFlag(
  flagKey: string,
  context: LaunchDarklyContext,
  defaultValue = false,
): Promise<boolean> {
  try {
    const client = await getLaunchDarklyClient(context);
    return client.variation(flagKey, defaultValue);
  } catch (error) {
    console.error(`Failed to check feature flag "${flagKey}":`, error);
    return defaultValue;
  }
}
